'use client'

import { ConfirmProvider } from '@/components/Confirm/ConfirmProvider'
import { PostHogProvider } from '@/components/Monitoring/PostHogProvider'
import { I18NContextProvider } from '@/context/I18NContext'
import type { Config } from '@carbonara/config/src/config'
import { type Language, getDictionary } from '@carbonara/translations'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useSearchParams } from 'next/navigation'
import { Suspense, lazy, useEffect, useState } from 'react'
const queryClient = new QueryClient()

const ReactQueryDevtoolsProduction = lazy(() =>
	import('@tanstack/react-query-devtools/build/modern/production.js').then(
		(d) => ({
			default: d.ReactQueryDevtools,
		}),
	),
)

interface Props {
	children: React.ReactNode
	posthog: Config['posthog']
	language: Language
}

export const Providers: React.FC<Props> = ({
	children,
	posthog,
	language: _lang,
}) => {
	const isDev = process.env.NODE_ENV === 'development'
	const searchParams = useSearchParams()
	const isPrint = searchParams.get('printPdf') === 'true'
	const language = isPrint ? 'nb' : _lang || 'en'
	const [showDevtools, setShowDevtools] = useState(isDev && !isPrint)

	useEffect(() => {
		window.toggleDevtools = () => setShowDevtools((old) => !old)
	}, [])

	const dictionary = getDictionary(language)

	return (
		<QueryClientProvider client={queryClient}>
			<PostHogProvider config={posthog}>
				<I18NContextProvider language={language} dictionary={dictionary}>
					<ConfirmProvider>{children}</ConfirmProvider>
					<Suspense fallback={null}>
						{showDevtools && (
							<ReactQueryDevtoolsProduction initialIsOpen={!isDev} />
						)}
					</Suspense>
				</I18NContextProvider>
			</PostHogProvider>
		</QueryClientProvider>
	)
}
